import {useState, useMemo} from 'react'
import {Strong} from '@/components/shared/Strong'
import {Stack} from '@/components/shared/Stack'
import {useQuery} from '@tanstack/react-query'
import {useParams} from 'react-router-dom'
import {ChevronDown, ChevronRight, History, Filter} from 'lucide-react'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {ClearFiltersButton} from '@/components/shared/ClearFiltersButton'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {Card, CardHeader, CardTitle, CardDescription, CardContent} from '@/components/ui/card'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {Pagination} from '@/components/ui/pagination'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {useServerPagination} from '@/hooks/use-server-pagination'
import {EffectiveAccessControllerGrantsForTargetSystemEndpoint} from '@/types/endpoint-contracts'
import type {EffectiveAccess} from '@/types/effective-access'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {formatDateOnly, formatTimestamp, formatRelativeTime} from '@/lib/date-utils'
import {PageLayout} from '@/components/shared/PageLayout'
import {TextLink} from '@/components/shared/TextLink'
import {EntityLink as EntityLinkChip} from '@/components/shared/EntityLink'
import {MutedText} from '@/components/shared/MutedText'
import {Icon} from '@/components/shared/Icon'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {InlineRow} from '@/components/shared/InlineRow'
import {SplitRow} from '@/components/shared/SplitRow'
import {CompactButton} from '@/components/shared/CompactButton'

const ALL_FILTER = 'all'

export function SystemGrantsDetail() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.analytics.systemGrantsPageTitle)
    const {id} = useParams<{ id: string }>()
    const [expandedGrants, setExpandedGrants] = useState<Set<string>>(new Set())
    const {systemTypes, environmentTypes} = useAppConfig()

    const {page, pageSize, setPage, setPageSize, pageSizeOptions, queryString} = useServerPagination({
        endpoint: EffectiveAccessControllerGrantsForTargetSystemEndpoint,
        defaultSortBy: EffectiveAccessControllerGrantsForTargetSystemEndpoint.sort.IDENTITY_PARTY_DISPLAY_NAME,
    })

    // Fetch system
    const {data: system, isLoading: systemLoading, error: systemError} = useQuery({
        queryKey: ['systems', id],
        queryFn: () => services.systems.detail(id!),
        enabled: !!id,
    })

    // Fetch grants for this system
    const {data: grantsData, isLoading: grantsLoading} = useQuery({
        queryKey: ['system-grants', id, queryString],
        queryFn: () => services.systems.grants(id!, Object.fromEntries(new URLSearchParams(queryString))),
        enabled: !!id,
    })

    const grants = useMemo(() => grantsData?.content ?? [], [grantsData])
    const totalItems = grantsData?.totalElements || 0
    const totalPages = grantsData?.totalPages || 0

    // Filter state
    const [identityFilter, setIdentityFilter] = useState<string>('all')
    const [operationFilter, setOperationFilter] = useState<string>('all')

    const toggleGrant = (grantId: string) => {
        const newExpanded = new Set(expandedGrants)
        if (newExpanded.has(grantId)) {
            newExpanded.delete(grantId)
        } else {
            newExpanded.add(grantId)
        }
        setExpandedGrants(newExpanded)
    }

    // Extract unique identities and operations for filters
    const identities = useMemo(() => {
        const idMap = new Map<string, string>()
        grants.forEach(g => idMap.set(g.identity.id, g.identity.displayName))
        return Array.from(idMap.entries()).sort((a, b) => a[1].localeCompare(b[1]))
    }, [grants])

    const operations = useMemo(() => {
        const opSet = new Set(grants.map(g => g.operation))
        return Array.from(opSet).sort()
    }, [grants])

    // Apply filters
    const filteredGrants = useMemo(() => {
        return grants.filter(g => {
            if (identityFilter !== 'all' && g.identity.id !== identityFilter) return false
            if (operationFilter !== 'all' && g.operation !== operationFilter) return false
            return true
        })
    }, [grants, identityFilter, operationFilter])

    const hasActiveFilters = identityFilter !== 'all' || operationFilter !== 'all'

    const clearFilters = () => {
        setIdentityFilter(ALL_FILTER)
        setOperationFilter(ALL_FILTER)
    }

    return (
        <PageLayout
            data-testid="system-grants-detail-page"
            variant="detail"
            title={system ? `${i18n.analytics.grantsFor} ${system.name}` : i18n.analytics.systemGrantsPageTitle}
            icon={CONCEPT_ICON.system}
            loading={systemLoading || grantsLoading}
            error={systemError ?? (!systemLoading && !grantsLoading ? notFoundIfMissing(system, 'SYSTEM_GRANTS') : undefined)}
            backTo={routes.effectiveAccess.systems.list()}
            description={system ? (
                <MutedText>{labelOf(systemTypes, system.systemType)} - {labelOf(environmentTypes, system.environment)}</MutedText>
            ) : undefined}
            actions={
                <TextLink
                    to={routes.systems.detail(id!)}
                    testId="link-system-detail"
                >
                    {i18n.analytics.viewSystemDetails}
                </TextLink>
            }
        >
            {system && (<>
            {/* Stats Card */}
            <DetailGrid columns={3} className="mb-6">
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>{i18n.analytics.totalGrants}</CardDescription>
                        <CardTitle className="text-2xl" data-testid="card-title-total-grants">{totalItems}</CardTitle>
                    </CardHeader>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>{i18n.analytics.activeGrants}</CardDescription>
                        <CardTitle className="text-2xl" data-testid="card-title-active-grants">
                            {grants.filter(g => g.active).length}
                        </CardTitle>
                    </CardHeader>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardDescription>{i18n.analytics.uniqueIdentities}</CardDescription>
                        <CardTitle className="text-2xl" data-testid="card-title-unique-identities">
                            {new Set(grants.map(g => g.identity.id)).size}
                        </CardTitle>
                    </CardHeader>
                </Card>
            </DetailGrid>

            {/* Grants Table */}
            <Card>
                <CardHeader>
                    <SplitRow>
                        <div>
                            <CardTitle data-testid="card-title-effective-access-grants">{i18n.analytics.effectiveAccessGrants}</CardTitle>
                            <CardDescription>
                                {`${filteredGrants.length} ${i18n.analytics.grantsFiltered({total: String(grants.length)})}${hasActiveFilters ? ` ${i18n.analytics.filtered}` : ''}`}
                            </CardDescription>
                        </div>
                        <InlineRow wrap>
                            <Icon icon={Filter} size="md" muted/>
                            <Select value={identityFilter} onValueChange={setIdentityFilter}>
                                <SelectTrigger className="w-[180px]" data-testid="filter-identity">
                                    <SelectValue placeholder={i18n.analytics.identity as string}/>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{i18n.analytics.allIdentities}</SelectItem>
                                    {identities.map(([idId, idName]) => (
                                        <SelectItem key={idId} value={idId}>{idName}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select value={operationFilter} onValueChange={setOperationFilter}>
                                <SelectTrigger className="w-[140px]" data-testid="filter-operation">
                                    <SelectValue placeholder={i18n.common.operation as string}/>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{i18n.analytics.allOperations}</SelectItem>
                                    {operations.map(op => (
                                        <SelectItem key={op} value={op}>{op}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <ClearFiltersButton hasActiveFilters={hasActiveFilters} onClick={clearFilters}/>
                        </InlineRow>
                    </SplitRow>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-8" data-testid="col-expand"></TableHead>
                                <TableHead data-testid="col-identity">{i18n.analytics.identity}</TableHead>
                                <TableHead data-testid="col-enforcement-object">{i18n.analytics.enforcementObject}</TableHead>
                                <TableHead data-testid="col-resource">{i18n.common.resource}</TableHead>
                                <TableHead data-testid="col-operation">{i18n.common.operation}</TableHead>
                                <TableHead data-testid="col-valid-from">{i18n.common.validFrom}</TableHead>
                                <TableHead data-testid="col-valid-to">{i18n.analytics.validTo}</TableHead>
                                <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredGrants.map((grant: EffectiveAccess) => (
                                <Collapsible key={grant.accessId} asChild open={expandedGrants.has(grant.accessId)}>
                                    <>
                                        <TableRow
                                            className="cursor-pointer hover:bg-muted/50"
                                            onClick={() => toggleGrant(grant.accessId)}
                                        >
                                            <TableCell>
                                                <CollapsibleTrigger asChild>
                                                    <CompactButton variant="ghost" size="xs" data-testid={`btn-expand-grant-${grant.accessId}`}>
                                                        {expandedGrants.has(grant.accessId)
                                                            ? <Icon icon={ChevronDown} size="md"/>
                                                            : <Icon icon={ChevronRight} size="md"/>
                                                        }
                                                    </CompactButton>
                                                </CollapsibleTrigger>
                                            </TableCell>
                                            <TableCell className="font-medium">
                                                <EntityLinkChip
                                                    value={grant.identity}
                                                    stopPropagation
                                                    testId={`link-identity-${grant.identity.id}`}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <EntityLinkChip
                                                    value={grant.enforcementObject}
                                                    stopPropagation
                                                    testId={`link-enforcement-object-${grant.enforcementObject.id}`}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {grant.resource ? (
                                                    <EntityLinkChip
                                                        value={grant.resource}
                                                        stopPropagation
                                                        testId={`link-resource-${grant.resource.id}`}
                                                    />
                                                ) : (
                                                    <MutedText>-</MutedText>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline">{grant.operation}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                {formatDateOnly(grant.validFrom)}
                                            </TableCell>
                                            <TableCell>
                                                {grant.permanent ? (
                                                    <Badge variant="secondary">{i18n.common.permanent}</Badge>
                                                ) : grant.validTo ? (
                                                    formatDateOnly(grant.validTo)
                                                ) : (
                                                    '-'
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={grant.active ? BadgeVariant.SUCCESS : BadgeVariant.SECONDARY}>
                                                    {grant.active ? i18n.common.active : i18n.common.inactive}
                                                </Badge>
                                            </TableCell>
                                        </TableRow>
                                        <CollapsibleContent asChild>
                                            <TableRow className="bg-muted/30">
                                                <TableCell colSpan={8} className="p-4">
                                                    <Stack gap="sm" className="text-sm">
                                                        <Strong className="mb-2">
                                                            <InlineRow gap="sm">
                                                                <Icon icon={History} size="md"/>
                                                                {i18n.analytics.sources} ({grant.sources.length})
                                                            </InlineRow>
                                                        </Strong>
                                                        <Table>
                                                            <TableHeader>
                                                                <TableRow>
                                                                    <TableHead data-testid="col-source-access-profile">{i18n.analytics.accessProfile}</TableHead>
                                                                    <TableHead data-testid="col-source-assignment-start">{i18n.analytics.assignmentStart}</TableHead>
                                                                    <TableHead data-testid="col-source-assignment-expires">{i18n.analytics.assignmentExpires}</TableHead>
                                                                    <TableHead data-testid="col-source-created-at">{i18n.common.createdAt}</TableHead>
                                                                </TableRow>
                                                            </TableHeader>
                                                            <TableBody>
                                                                {grant.sources.map((source) => (
                                                                    <TableRow key={source.sourceId}>
                                                                        <TableCell>
                                                                            <EntityLinkChip
                                                                                value={source.accessProfile}
                                                                                testId={`link-access-profile-${source.accessProfile.id}`}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {formatTimestamp(source.assignmentStartAt)}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {source.assignmentPermanent ? (
                                                                                <Badge
                                                                                    variant="secondary">{i18n.common.permanent}</Badge>
                                                                            ) : source.assignmentExpiresAt ? (
                                                                                formatTimestamp(source.assignmentExpiresAt)
                                                                            ) : (
                                                                                '-'
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell className="text-muted-foreground">
                                                                            {formatRelativeTime(source.createdAt)}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        </CollapsibleContent>
                                    </>
                                </Collapsible>
                            ))}
                            {filteredGrants.length === 0 && (
                                <TableEmptyState
                                    colSpan={8}
                                    hasFilters={hasActiveFilters}
                                    matchMessage={i18n.analytics.noGrantsMatchFilters}
                                    emptyMessage={i18n.analytics.noGrantsForSystem}
                                />
                            )}
                        </TableBody>
                    </Table>

                    {/* Pagination */}
                    {totalItems > 0 && (
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalItems={totalItems}
                            totalPages={totalPages}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            pageSizeOptions={pageSizeOptions}
                        />
                    )}
                </CardContent>
            </Card>
            </>)}
        </PageLayout>
    )
}
